/* Tuning constants — must match prototype values pixel-for-pixel */

// Match shape
export const STEPS_TOTAL = 15;
// Vertical distance between two consecutive holds. Sized to exactly one
// climber-height (CLIMBER_BASE_H * CLIMBER_SCALE ≈ 67px) so the climber fits
// neatly under the next target hold — looks like a real speed-climbing wall
// where each grab is one body-length up. Zoomed ~1.45× from the original
// prototype values so the character + wall read clearly on small phone screens
// (fewer holds visible at once, but each one is big and legible).
export const STEP_PX = 67;
export const LANE_H = 420;
export const HEADER_H = 44;
export const HOLD_BAND_H = STEPS_TOTAL * STEP_PX + 100;
export const FOCUS_FROM_BOTTOM = 150;
export const PATH_MAX_X = 62;
export const HOLD_BASE_OFFSET = 88;
export const START_LINE_Y = 10;

// Hold geometry (rect / ladder-rung)
export const HOLD_RECT_W = 34;
export const HOLD_RECT_H = 11;

// Climber sprite
export const CLIMBER_BASE_W = 56;
export const CLIMBER_BASE_H = 84;
export const CLIMBER_SCALE = 0.8;

// Pace / WR
export const WR_TIME = 8.5;

// AI
export const AI_DEFAULT_SPEED_S = 1.4;
export const AI_SPEED_JITTER = 0.6;

// Skills — these are fallback constants for offline/local play. Online matches
// read live values from SettingsService (admin-tunable, 0.1s precision).
export const SKILL_DISRUPT_CD_S = 8;
export const SKILL_SHIELD_CD_S = 8;
export const SHIELD_DURATION_MS = 1000;
export const DISRUPT_VORTEX_MS = 1200;
export const DISRUPT_RESHUFFLE_COUNT = 3;
// Holds flash before they actually swap colors so the player has a visual
// warning that something is about to change. Matches the 1s "delay after
// activation" originally specified for the Disrupt skill.
export const DISRUPT_FLASH_MS = 1000;

// Match flow
export const CORRECT_TAP_POSE_RESET_MS = 520;
export const WRONG_TAP_POSE_RESET_MS = 820;
export const WRONG_TAP_FLASH_MS = 220;
export const MATCH_OVER_OVERLAY_MS = 1800;

// Phone reference design
export const PHONE_W = 390;
export const PHONE_H = 844;

// MMR — used by matchmaking for fair pairing. Visible rank/division/stars
// progresses independently (Wild Rift style).
export const MMR_K_FACTOR = 32;
export const MMR_START = 1000;
export const MMR_FLOOR = 0;
export const WIN_STREAK_BONUS_AFTER = 3;
export const WIN_STREAK_BONUS_PER = 2;

// ── Dynamic K-factor ─────────────────────────────────────────────────
// New accounts converge to their true rating fast (high K during placement);
// elite players' ratings stay stable (low K). Everyone else uses MMR_K_FACTOR.
export const PLACEMENT_MATCHES = 10;
export const MMR_K_PLACEMENT = 48;
export const MMR_ELITE_THRESHOLD = 2400;
export const MMR_K_ELITE = 20;

// ── Inactivity MMR/rank decay ────────────────────────────────────────
// Ranked-ladder only, high tiers only — a competitive ladder shouldn't let an
// inactive account squat at the top. After DECAY_GRACE_DAYS without a ranked
// match the player loses DECAY_STARS_PER_PERIOD star(s) every DECAY_PERIOD_DAYS.
// DECAY_MIN_TIER_INDEX gates it to diamond+ (index into RANK_TIER_ORDER).
export const DECAY_GRACE_DAYS = 7;
export const DECAY_PERIOD_DAYS = 7;
export const DECAY_STARS_PER_PERIOD = 1;
export const DECAY_MIN_TIER_INDEX = 4; // RANK_TIER_ORDER[4] = 'diamond'
export const DECAY_SWEEP_INTERVAL_MS = 24 * 60 * 60 * 1000; // daily

// ── Visible rank progression (Wild-Rift-style stars) ─────────────────
// Tier order, lowest → highest. The player climbs from bronze IV up.
export const RANK_TIER_ORDER = [
  'bronze',
  'silver',
  'gold',
  'platinum',
  'diamond',
  'master',
  'challenger',
] as const;
// Stars needed to fill a division. Higher tiers cost more per division so the
// climb naturally slows down.
export const STARS_PER_DIVISION: Record<(typeof RANK_TIER_ORDER)[number], number> = {
  bronze: 3,
  silver: 3,
  gold: 4,
  platinum: 4,
  diamond: 5,
  master: 5,
  challenger: 6,
};
// Number of divisions per tier (IV, III, II, I).
export const DIVISIONS_PER_TIER = 4;
// Win-streak threshold: after this many consecutive wins the next win earns
// +1 extra star.
export const RANK_STREAK_BONUS_AFTER = 2;
export const RANK_STREAK_BONUS_PER = 1;

// Rewards
export const REWARD_WIN_COINS = 120;
export const REWARD_LOSE_COINS = 30;
export const REWARD_WIN_XP = 85;
export const REWARD_LOSE_XP = 20;

// Palette
export const PALETTE = {
  red: '#E63946',
  blue: '#3B82F6',
  yellow: '#F4C724',
  purple: '#9B5DE5',
  cyan: '#22D3EE',
} as const;

export const PALETTE_HEX_TO_NUM = {
  red: 0xe63946,
  blue: 0x3b82f6,
  yellow: 0xf4c724,
  purple: 0x9b5de5,
  cyan: 0x22d3ee,
} as const;

// Colorblind alt palette (deuteranopia-friendly)
export const PALETTE_COLORBLIND = {
  red: '#D81B60',
  blue: '#1E88E5',
  yellow: '#FFC107',
  purple: '#A0522D',
  cyan: '#22D3EE',
} as const;

// Wall ID
export const WALL_CHROMA_ASCENT = 'chroma_ascent_wall';

// Tap rate-limit — DISABLED (0). Even at 30ms we observed desync: client
// predicts step+1 optimistically per tap, server rejects throttled taps, so
// client sends "tap for step N+1" colors while server is still at step N-2.
// Server then sees mismatched colors → marks them wrong → climber falls →
// server step never reaches the win condition. A short race (~15s, ~20 taps)
// doesn't need socket throttling; payloads are <100 bytes.
export const MIN_TAP_INTERVAL_MS = 0;

// Net
export const HEARTBEAT_MS = 1000;
export const RECONNECT_BUFFER_MS = 2000;
export const MATCHMAKING_AI_FALLBACK_MS = 8000;

// ── 2v2 Team mode ────────────────────────────────────────────────────
// A team-duel race is longer so both teammates get meaningful turns.
export const STEPS_TOTAL_2V2 = 40;
// Alternating mode: each member taps this many consecutive steps, then hands
// off to the teammate.
export const ALT_TURN_STEPS = 5;
// Combo gates — UNLOCK the right to cast (charges are still drawn from the
// player's inventory). Combo is team-shared and resets to 0 on any wrong tap.
export const ALT_SHIELD_COMBO = 5; // alternating: combo ≥5 → shield castable
export const ALT_DISRUPT_COMBO = 10; // alternating: combo ≥10 → disrupt castable
export const SPLIT_SKILL_COMBO = 5; // split-color: combo ≥5 → both castable
// Two teammates casting the SAME skill within this window → enhanced effect.
export const SIMULTANEOUS_SKILL_MS = 500;
// Enhanced-effect tuning (triggered by a simultaneous co-cast in alternating).
export const TEAM_SHIELD_BONUS_MULT = 1.5; // shield duration ×1.5
export const TEAM_DISRUPT_FLASH_MULT = 0.5; // disrupt flash halved → applies 2× faster

// 2v2 AI fallback — if a queued duo waits longer than this, pair it against a
// bot duo so it never hangs. Longer than the solo fallback (8s): a real 2v2
// opponent is worth waiting a bit more for before giving up on humans.
export const DUO_AI_FALLBACK_MS = 20000;
// Bot duo difficulty — deliberately TOUGHER than the solo bot (which climbs at
// ~1.4s/step). The 2v2 fallback opponent climbs faster with less hesitation and
// casts skills more aggressively, so it pressures a coordinated human duo.
export const DUO_BOT_START_DELAY_MS = 800;     // first step after MATCH_START
export const DUO_BOT_STEP_MIN_MS = 340;        // hard floor between steps
export const DUO_BOT_STEP_AVG_S = 0.85;        // mean step cadence (vs solo 1.4s)
export const DUO_BOT_STEP_JITTER_S = 0.35;     // ± randomness around the mean
export const DUO_BOT_SKILL_MIN_MS = 1200;      // base gap between skill attempts
export const DUO_BOT_SKILL_JITTER_MS = 1000;   // ± randomness on skill cadence
